import { useMemo } from 'react';

import { PrimaryButton, SecondaryButton } from 'components/Button';
import { ProposalQuery } from 'generated/graphql';
import { formatCents, PLUS_TAX_TEXT } from 'utilities/money';

import MultiHotelIndicator from './MultiHotelIndicator';
import ProposalHotelCard from './ProposalHotelCard';

type ProposalOptionFromQuery = ProposalQuery['proposal']['proposalOptions'][0];

interface ProposalOptionCardProps {
  option: ProposalOptionFromQuery;
  optionIndex: number;
  totalNights: number;
  isPreferred: boolean;
  onChooseOption: (optionId: string) => void;
  showSelect: boolean;
  isChoosing?: boolean;
  selectedLineItems: Record<string, boolean>;
  onLineItemToggle: (itemId: string) => void;
  hasSelectedOption?: boolean;
  readOnly?: boolean;
}

const ProposalOptionCard = ({
  option,
  optionIndex,
  totalNights,
  isPreferred,
  onChooseOption,
  showSelect,
  isChoosing = false,
  selectedLineItems,
  onLineItemToggle,
  hasSelectedOption = false,
  readOnly = false,
}: ProposalOptionCardProps) => {
  const totalPrice = useMemo(() => {
    const basePrice = option.totalPrice;

    // Calculate line items adjustment
    let lineItemsAdjustment = 0;
    option.hotels.forEach((hotel) => {
      hotel.lineItems.forEach((item) => {
        const wasSelected = item.selected;
        const isNowSelected = selectedLineItems[item.id];

        if (isNowSelected && !wasSelected) {
          lineItemsAdjustment += item.price;
        } else if (!isNowSelected && wasSelected) {
          lineItemsAdjustment -= item.price;
        }
      });
    });

    return basePrice + lineItemsAdjustment;
  }, [option.totalPrice, option.hotels, selectedLineItems]);

  const hasMultipleHotels = option.hotels.length > 1;

  const cardClasses = isPreferred
    ? 'border-accentGreen/30 ring-2 ring-accentGreen/20 shadow-accentGreen/10'
    : 'border-gray-200/60 hover:border-gray-300/80';

  return (
    <div className="mb-6">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-medium text-gray-500">
          Option {optionIndex + 1}
        </span>
        {hasMultipleHotels && (
          <MultiHotelIndicator hotelCount={option.hotels.length} />
        )}
      </div>

      <div
        className={`group relative overflow-hidden rounded-2xl border bg-gray-50 p-4 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-xl sm:p-6 lg:p-8 ${cardClasses}`}
      >
        {/* Subtle accent line */}
        <div
          className={`absolute inset-x-0 top-0 h-1 ${isPreferred ? 'bg-accentGreen' : 'bg-gray-200'}`}
        />

        {isPreferred && (
          <div className="absolute right-3 top-3 sm:right-6 sm:top-6">
            <div className="inline-flex items-center rounded-full bg-accentGreen px-3 py-1.5 text-xs font-semibold text-white shadow-lg sm:px-4 sm:py-2 sm:text-sm">
              <div className="mr-1.5 size-1.5 animate-pulse rounded-full bg-white/80 sm:mr-2 sm:size-2" />
              <span className="hidden sm:inline">Preferred Choice</span>
              <span className="sm:hidden">Preferred</span>
            </div>
          </div>
        )}

        {/* Hotels */}
        <div className="space-y-6">
          {option.hotels.map((hotel, hotelIndex) => (
            <div key={hotel.id}>
              {hotelIndex > 0 && (
                <div className="mb-6 border-t border-gray-200/60" />
              )}
              <ProposalHotelCard
                hotel={hotel}
                hotelIndex={hotelIndex}
                totalNights={totalNights}
                selectedLineItems={selectedLineItems}
                onLineItemToggle={onLineItemToggle}
                disabled={hasSelectedOption}
                readOnly={readOnly}
              />
            </div>
          ))}
        </div>

        {/* Option Summary */}
        <div className="mt-6 border-t border-gray-200/60 pt-6">
          <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
            <div className="space-y-1">
              <div className="text-sm text-gray-600">
                Total Rooms:{' '}
                <span className="font-semibold text-gray-900">
                  {option.totalRooms}
                </span>
              </div>
              <div className="text-sm text-gray-600">
                Total Price:{' '}
                <span className="font-semibold text-gray-900">
                  {formatCents(totalPrice)} {PLUS_TAX_TEXT}
                </span>
              </div>
            </div>

            {/* Action Button */}
            {showSelect && (
              <div className="mt-4 sm:mt-0">
                {isPreferred ? (
                  <PrimaryButton className="w-full sm:w-auto">
                    <span className="flex items-center justify-center">
                      <svg
                        className="mr-2 size-4"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      Selected
                    </span>
                  </PrimaryButton>
                ) : !hasSelectedOption ? (
                  <SecondaryButton
                    className="w-full sm:w-auto"
                    onClick={() => onChooseOption(option.id)}
                    disabled={isChoosing}
                  >
                    {isChoosing ? (
                      <span>Processing...</span>
                    ) : (
                      <>
                        <span className="hidden sm:inline">
                          Choose This Option
                        </span>
                        <span className="sm:hidden">Choose Option</span>
                      </>
                    )}
                  </SecondaryButton>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProposalOptionCard;
